import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  useTabContentContext,
} from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Context';
import TableTitle
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Content/TabTemplatePicker/Allocations/TableView/CollapsableTable/TableTitle';
import fr from 'facet-rest';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import moment from 'moment';
import CollapsableTable from './CollapsableTable';

export const RESOURCE_TYPES = {
  highlightedPrimary: 'highlightedResourceTableDataCell',
};

const styles = theme => ({
  root: {
    margin: theme.spacing(0, 4),
  },
});

export const getTabColumns = (columnCollection) => {
  return columnCollection?.resources().list().map((column) => (
    {
      title: column.attr('label'),
      key: column.id(),
      warningMessage: column.attr('warningMessage'),
    }
  ));
};

// Structure table data
// TODO: Refactor Scenario Selection Detail Table to use the resource directly instead of this
export const getRowSetData = (rowSetCollection) => {
  return rowSetCollection?.resources().list().map((rowSet) => {
    return rowSet.attr('rows').resources().list().map((row, i) => {
      return ({
        id: row.id(),
        label: row.attr('label'),
        altLabel: row.attr('altLabel'),
        rowDetailsUrl: row.relationship(fr.relationships.rowDetails.name)?.link('related').url(),
        subLabel: row.attr('subLabel'),
        cellType: row.attr('cellType'),
        hasEmphasis: row.isTruthy('hasEmphasis'),
        rowResource: row,
        data: row.attr('cells').resources().list().map((cell) => {
          return (
            {
              id: cell.id(),
              value: cell.attr('value'),
              percentValue: cell.attr('percentValue'),
              altValue: cell.attr('altValue'),
              cellType: row.attr('cellType'),
              cellResource: cell,
              rowResource: row,
              hint: cell.meta().field('value').attr('hint'),
            }
          );
        }),
        rowSetLabel: i === 0 && rowSet.attr('label') && rowSet.attr('label'),
        rowSets: row.attr('rowSets') && getRowSetData(row.attr('rowSets')),
        rowSummarySet: row.attr('rowSummarySet') && getRowSetData(row.attr('rowSummarySet')),
        hint: row.meta().field('subLabel').attr('hint'),
      });
    });
  }).flat();
};

const OutcomeAllocationsTable = ({
  className,
  classes,
  height,
  tableResourceOverride,
  showColumnLabels,
  noIndividualCellSubmit,
}) => {
  const TOTAL_CELL_ID = 'total';
  const { activeTabResource, activeTabResourceType, setActiveTabResource } = useTabContentContext();
  const { refreshPageContentCollection } = usePageContentContext();
  const [saving, setSaving] = useState(false);
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();

  let tableResource = activeTabResource;
  if (tableResourceOverride) {
    tableResource = tableResourceOverride;
  }

  const onSubmit = async (tableSectionResource) => {
    try {
      setSaving(true);
      await tableSectionResource.submit();
      refreshPageContentCollection();
      displaySuccess('Saved account allocations');
    } catch (err) {
      if (err.errors) {
        displayErrorMessage(err, err.errors);
      } else {
        displayErrorMessage(err, 'Error: failed to save account allocations');
      }
    } finally {
      setSaving(false);
    }
  };

  const onCellFormSubmitOverride = (rowResource, cellResource) => {
    cellResource.attr('value', cellResource.attr('value'));
    cellResource.attr('percentValue', cellResource.attr('percentValue'));
    cellResource.attr('userModified', 'true');

    if (cellResource.attr('when') === 'allMonths') {
      rowResource.attr('cells').resources().list().forEach(resource => {
        if (resource.id().includes(TOTAL_CELL_ID)) {
          return;
        }
        resource.attr('value', cellResource.attr('value'));
        resource.attr('percentValue', cellResource.attr('percentValue'));
        resource.attr('userModified', 'true');
      });
    }

    // If it's all future months
    if (cellResource.attr('when') === 'allFutureMonths') {
      // Skip every cell before the cell we're looking at and then set the value for every cell after the current cell
      let foundCell = false;
      rowResource.attr('cells').resources().list().forEach(resource => {
        if (resource.id().includes(TOTAL_CELL_ID)) {
          return;
        }

        if (resource.id() === cellResource.id()) {
          foundCell = true;
        }

        if (!foundCell) {
          return;
        }
        resource.attr('value', cellResource.attr('value'));
        resource.attr('percentValue', cellResource.attr('percentValue'));
        resource.attr('userModified', 'true');
      });
    }

    if (cellResource.attr('when') === 'untilEndOfCalendarYear') {
      // Skip every cell before the cell we're looking at and then set the value for every cell after the current cell
      let foundCell = false;
      rowResource.attr('cells').resources().list().forEach(resource => {
        if (resource.id().includes(TOTAL_CELL_ID)) {
          return;
        }

        if (resource.id() === cellResource.id()) {
          foundCell = true;
        }

        if (!foundCell) {
          return;
        }

        // If we find a cell that is in a future year, then stop
        if (moment(cellResource.attr('overrideStartDate')).year() < moment(resource.attr('overrideStartDate')).year()) {
          return;
        }

        resource.attr('value', cellResource.attr('value'));
        resource.attr('percentValue', cellResource.attr('percentValue'));
        resource.attr('userModified', 'true');
      });
    }

    // If it's custom
    if (cellResource.attr('when') === 'custom') {
      // Skip every cell before the cell we're looking at and then set the value for every cell after the current cell every x months
      // until the end date
      let foundCell = false;
      let foundLastCell = false;
      let numCellsAfterCurrentCell = 0;
      for (const resource of rowResource.attr('cells').resources().list()) {
        if (resource.id().includes(TOTAL_CELL_ID)) {
          continue;
        }

        if (resource.id() === cellResource.id()) {
          foundCell = true;
        }

        if (!foundCell) {
          continue;
        }

        // Don't go past the last cell
        if (foundLastCell) {
          continue;
        }

        if (!foundLastCell && !!cellResource.attr('overrideEndDate')) {
          const idPart = moment(cellResource.attr('overrideEndDate')).format('YYYY-MM');
          foundLastCell = resource.id().includes(idPart);
        }

        // If it's not on the frequency, skip
        const mod = numCellsAfterCurrentCell % cellResource.attr('overrideRepeatFrequency');
        if (mod !== 0) {
          numCellsAfterCurrentCell++;
          continue;
        }

        resource.attr('value', cellResource.attr('value'));
        resource.attr('percentValue', cellResource.attr('percentValue'));
        resource.attr('userModified', 'true');

        numCellsAfterCurrentCell++;
      }
    }

    setActiveTabResource(activeTabResource);
  };

  const onLockRow = (rowResource) => {
    let lockValue = true;
    // If everything is locked, then unlock everything
    if (rowResource.attr('cells').resources().list().every(resource => resource.id().includes(TOTAL_CELL_ID) || resource.isTruthy('userModified'))) {
      lockValue = false;
    }

    rowResource.attr('cells').resources().list().forEach(resource => {
      if (resource.id().includes(TOTAL_CELL_ID)) {
        return;
      }
      resource.attr('userModified', lockValue.toString());
      resource.attr('warningMessage', null);
    });

    setActiveTabResource(activeTabResource);
  };

  return (
    <Box
      className={classNames(classes.root, className)}
    >
      <CollapsableTable
        columns={getTabColumns(tableResource?.attr('columnLabels'))}
        data={getRowSetData(tableResource?.attr('rowSets'))}
        PlaceholderMessageProps={{ message: 'No Data' }}
        TopContent={(<TableTitle
          showButton={['accountActionAllocationDetails', 'sacStepDetails', 'allocationDetails'].includes(activeTabResourceType)}
          tableName={tableResource.attr('tableName')}
          autoAllocateAllUrl={tableResource.relationship(fr.relationships.autoAllocateOutcome.name)?.link('related').url()}
          onSubmit={(noIndividualCellSubmit) ? () => onSubmit(tableResource) : null}
          saving={saving}
        />)}
        test={'outcome-allocation-table'}
        height={height}
        showColumnLabels={showColumnLabels}
        onCellFormSubmitOverride={noIndividualCellSubmit ? onCellFormSubmitOverride : null} // Only do this if each cell doesn't submit automatically
        onLockRow={noIndividualCellSubmit ? onLockRow : null} // Only do this if each cell doesn't submit automatically
      />
    </Box>
  );
};

OutcomeAllocationsTable.defaultProps = {
  className: '',
  height: null,
  tableResourceOverride: null,
  showColumnLabels: true,
  noIndividualCellSubmit: false,
};

OutcomeAllocationsTable.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  tableResourceOverride: PropTypes.object,
  height: PropTypes.number,
  showColumnLabels: PropTypes.bool,
  noIndividualCellSubmit: PropTypes.bool,
};

export default withStyles(styles)(OutcomeAllocationsTable);
